import { Fragment } from "react"

/*
 * this function allows us to nest react components within translation strings
 *
 * for the following phrases: {
 *   description_text: "This information is %{important}.",
 *   important_text: "very important"
 * }
 *
 * you can render the important_text as bold like so:
 * polyglot.t(
 *   "description_text",
 *   {
 *     important: (
 *       <strong>
 *         {poylglot.t("important_text")}
 *       </strong>
 *     ),
 *   }
 * )
 *
 * https://github.com/airbnb/polyglot.js/blob/2e29439a75c1f38a905085575555ae471db1f3fe/index.js#L250
 */
export function replaceReact(
  // in the polyglot source code "this" is a string
  this: string,
  // the regex that creates matches for the interpolation syntax
  interpolationRegex: RegExp,
  // the callback that renders the correct item from the options for each match
  callback: (expression: string, argument: string) => string,
) {
  // eslint-disable-next-line @typescript-eslint/no-this-alias
  const phrase = this // the phrase from a translation file

  let i = 0
  let renderAsReact = false
  const children = []
  const matches = Array.from(phrase.matchAll(interpolationRegex))

  matches.forEach((match) => {
    if (typeof match.index === "number") {
      // add the part of the phrase string up to the next match
      if (match.index > i) {
        children.push(
          phrase.substring(
            i,
            match.index,
          ),
        )
      }

      // add the next match from the options object using the callback function
      const interpolatedValue = callback(
        match[0], // the whole match, ex: "%{name}"
        match[1], // the captured string, ex: "name"
      )

      children.push(interpolatedValue)

      if (
        typeof interpolatedValue !== "string" &&
        typeof interpolatedValue !== "number"
      ) {
        renderAsReact = true
      }

      i = match.index + match[0].length
    }
  })

  if (i < phrase.length) {
    // add the part of the phrase string after the last match
    children.push(phrase.substring(i))
  }

  // render as react if any of the intrpolated values are not strings
  if (renderAsReact) {
    return children.map((child, index) => (
      <Fragment key={index}>
        {child}
      </Fragment>
    ))
  }

  // otherwise render as a string, this allows sorting translated strings
  return children.join("")
}
